// Reusable schema builders for SEO

const SITE_URL = "https://claimbeast.com";
const PHONE = "+1-386-747-9440";
const LOGO_URL = `${SITE_URL}/assets/claim-beast-logo.png`;

const BUSINESS_DESCRIPTION =
  "Florida & Georgia licensed public adjusters based in Riverview, FL serving Tampa Bay homeowners. We fight insurance carriers to recover the full payout you're owed on denied, underpaid, hurricane, wind, hail, fire, water, and roof claims. No win, no fee.";

const DEFAULT_AREAS_SERVED = [
  "Riverview FL",
  "Tampa FL",
  "Brandon FL",
  "Apollo Beach FL",
  "Ruskin FL",
  "Sun City Center FL",
  "Wesley Chapel FL",
  "Plant City FL",
  "Lutz FL",
  "Valrico FL",
  "Town N Country FL",
  "Carrollwood FL",
  "Temple Terrace FL",
  "Fishhawk FL",
  "Gibsonton FL",
  "Bloomingdale FL",
];

export const localBusinessSchema = ({
  areaServed = [],
  geo,
  city,
} = {}) => ({
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "ProfessionalService"],
  "@id": city
    ? `${SITE_URL}/public-adjuster-${city}#business`
    : `${SITE_URL}/#business`,
  name: "Claim Beast Public Adjusters",
  alternateName: "Claim Beast",
  description: BUSINESS_DESCRIPTION,
  // Social + Google Business Profile — helps Google's Knowledge Graph
  // connect this site to verified off-site profiles
  sameAs: [
    "https://www.facebook.com/claimbeast/",
    "https://maps.google.com/?cid=129351785190383915",
  ],
  image: {
    "@type": "ImageObject",
    url: LOGO_URL,
    width: 512,
    height: 512,
  },
  logo: LOGO_URL,
  telephone: PHONE,
  email: "Joe@claimbeast.com",
  priceRange: "Free Consultation",
  url: SITE_URL,
  slogan: "Your Loss. Our Fight. Your Payout.",
  knowsAbout: [
    "Public Adjusting",
    "Insurance Claim Negotiation",
    "Hurricane Damage Claims",
    "Roof Damage Claims",
    "Wind Damage Claims",
    "Hail Damage Claims",
    "Water Damage Claims",
    "Fire Damage Claims",
    "Denied Insurance Claims",
    "Underpaid Insurance Claims",
    "Florida Insurance Law",
    "Xactimate Estimating",
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Riverview",
    addressRegion: "FL",
    addressCountry: "US",
  },
  geo: geo
    ? { "@type": "GeoCoordinates", latitude: geo.latitude, longitude: geo.longitude }
    : {
        "@type": "GeoCoordinates",
        latitude: 27.8661,
        longitude: -82.3265,
      },
  areaServed: (areaServed.length ? areaServed : DEFAULT_AREAS_SERVED).map(
    (a) => ({ "@type": "City", name: a })
  ),
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ],
    opens: "00:00",
    closes: "23:59",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Public Adjusting Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Free Claim Review",
          description: "No-obligation review of your insurance claim and policy.",
        },
      },
    ],
  },
});

export const serviceSchema = ({ name, description, slug, areaServed }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: name,
  name,
  description,
  url: `${SITE_URL}/${slug}`,
  provider: { "@id": `${SITE_URL}/#business` },
  areaServed: (areaServed || DEFAULT_AREAS_SERVED).map((a) => ({
    "@type": "City",
    name: a,
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.answer,
    },
  })),
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

// Person schema for "Meet The Beasts" page
export const personSchema = ({ name, role, slug, image, sameAs = [], description }) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name,
  jobTitle: role,
  url: `${SITE_URL}/about#${slug}`,
  image,
  description,
  worksFor: { "@id": `${SITE_URL}/#business` },
  ...(sameAs.length ? { sameAs } : {}),
});
